import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../api/axios";

/* ═══════════════════════════════════════════════════════════════
   STOCK MANAGER

   TWO CHANNELS, TWO INDEPENDENT POOLS. One product can sell both ways:

     • SHELF (sellsPrepacked)  → one balance per pack SKU, counted in
       PACKS. Orders decrement it and cancel/refund restores it, so the
       pack numbers below ARE the shelf.
     • BULK  (sellsBulkMilled) → the bin, held in GRAMS on the server
       and shown here in KG, which is also the unit its price is quoted
       in.

   The pools are INDEPENDENT. This screen sets each one directly and
   there is deliberately NO pack-from-bulk operation: nothing here (or
   anywhere in this system) converts kg in the bin into pouches on the
   shelf, or the reverse. Stock in each pool is managed on its own.

   STOCK IS PER STORE, so every read and write on this screen names one:
     packs   GET  /stock/balances?storeId=&productId=
             POST /admin/bulk/stock/apply   { storeId, mode, lines[] }
     bin     GET  /admin/bulk/grains/{productId}?storeId=
             POST /admin/bulk/grains/{productId}/opening | /restock
             PUT  /admin/bulk/grains/{productId}/price
     log     GET  /stock/moves?productId=&storeId=&limit=

   The store picker is pinned and hidden for a store-bound admin, whose
   own store is the only one the server will let it count.
═══════════════════════════════════════════════════════════════ */

const BRAND = {
  forest: "var(--mly-heading)",
  pine:   "var(--mly-accent)",
  gold:   "var(--mly-accent)",
  honey:  "var(--mly-accent-2)",
  cream:  "var(--mly-surface-2)",
  sand:   "var(--mly-border)",
  ink:    "var(--mly-text)",
  stone:  "var(--mly-text-soft)",
};

/* TODO(cutover): server does not return StoreSetting.lowStockThreshold —
   GET /admin/store-settings has no such field, so the default stands. */
const LOW_STOCK_DEFAULT = 10;
const BULK_KEY = "__bulk__";   // the bulk bin of a product that ALSO sells packs
const DEFAULT_ROW = { mode: "set", qty: "", note: "", saving: false, err: "", msg: "" };

/* what a move type did, for the log pill — the server records the kind of
   event, not the "set"/"add" the operator typed */
const MOVE_LABEL = {
  opening:   "OPENING",
  grn:       "RESTOCK",
  count_adj: "COUNT",
  sale:      "SALE",
  transfer:  "TRANSFER",
  return:    "RETURN",
  loss:      "LOSS",
};

const LABEL = {
  fontSize: 11, fontWeight: 700, letterSpacing: 1,
  color: BRAND.stone, textTransform: "uppercase",
};

const TH = {
  padding: "9px 12px", textAlign: "left", fontWeight: 700,
  color: BRAND.stone, fontSize: 10.5, textTransform: "uppercase",
  letterSpacing: 0.6, borderBottom: `1px solid ${BRAND.sand}`,
  whiteSpace: "nowrap",
};

const TD = { padding: "9px 12px", fontSize: 13, color: BRAND.ink, verticalAlign: "middle" };

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const formatWhen = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const date = `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${date} ${time}`;
};

const todayIso = () => new Date().toISOString().split("T")[0];

/* ── pack rows: the detail call's sibling packs[], with the on-hand number
      joined in from /stock/balances (the pack rows themselves carry none) ── */
const packsOf = (packRows, balances) =>
  (packRows || [])
    .filter((v) => v && v.soldBy === "pack" && v.isActive !== false)
    .map((v, i) => {
      const sku   = v.sku ?? "";
      const label = (v.label ?? sku ?? "—") || "—";
      return {
        key:   sku || `idx-${i}`,
        sku,
        label,
        stock: sku && balances ? (num(balances[sku]) ?? null) : null,
      };
    });

/* which channels this product sells through — two always-present booleans */
const channelsOf = (product) => ({
  bulk:  !!product?.sellsBulkMilled,
  shelf: !!product?.sellsPrepacked,
});

/* the shelf total: the packs whose balance is known, added up */
const totalOf = (packs) => {
  const known = packs.filter((p) => p.stock !== null);
  return known.length ? known.reduce((s, p) => s + p.stock, 0) : null;
};

/* ── small stock pill ── */
const StockPill = ({ value, threshold = LOW_STOCK_DEFAULT, size = 13 }) => {
  if (value === null || value === undefined) {
    return <span style={{ color: "var(--mly-text-muted)", fontSize: 12 }}>—</span>;
  }
  const out    = value === 0;
  const low    = !out && value <= threshold;
  const color  = out ? "#ef4444" : low ? "#8A6516" : "#16a34a";
  const bg     = out ? "#fff5f5" : low ? "#FDF6E3" : "#f0fdf4";
  const border = out ? "#fecaca" : low ? "#C9952A" : "#bbf7d0";
  return (
    <span style={{
      background: bg, color, border: `1px solid ${border}`,
      fontWeight: 800, fontSize: size, padding: "3px 12px",
      borderRadius: 20, whiteSpace: "nowrap", display: "inline-block",
    }}>
      {value}
    </span>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════════ */
const StockEntry = () => {
  const navigate = useNavigate();

  /* ── search ── */
  const [query,      setQuery]      = useState("");
  const [results,    setResults]    = useState([]);
  const [searching,  setSearching]  = useState(false);
  const [pageError,  setPageError]  = useState("");
  const searchSeq = useRef(0);

  /* ── the stores this admin may count ──
     Already narrowed server-side: the whole estate for a superadmin, exactly
     their own store for anyone else. A list of one is the only store this
     operator can touch — pin to it and show no picker. */
  const [stores,  setStores]  = useState([]);
  const [storeId, setStoreId] = useState("");

  /* ── selection ── */
  const [selected,   setSelected]   = useState(null);   // the product row
  const [packRows,   setPackRows]   = useState([]);     // detail call's packs[]
  const [balances,   setBalances]   = useState({});     // sku -> packs on hand
  const [grain,      setGrain]      = useState(null);   // the bulk bin, or null
  const [loadingOne, setLoadingOne] = useState(false);

  /* ── per-row form state, keyed by pack key ── */
  const [rows, setRows] = useState({});

  /* ── adjustment log ── */
  const [log,        setLog]        = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const threshold = LOW_STOCK_DEFAULT;

  /* ── bulk per-kg price ──
     The bin's price is a price row of its own, so the product form shows it
     read-only and points here. Without this editor there is nowhere in the
     portal to change a bulk price at all. */
  const [priceMrp,    setPriceMrp]    = useState("");
  const [priceSp,     setPriceSp]     = useState("");
  const [priceSaving, setPriceSaving] = useState(false);
  const [priceMsg,    setPriceMsg]    = useState("");

  /* ── which stores this account may count ── */
  useEffect(() => {
    let alive = true;
    api.get("/admin/store-settings/overview")
      /* the payload is literally { data:[…], meta:{…} } — the inner `data`
         is not a leftover Node wrapper */
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setStores(list);
        if (list.length === 1) setStoreId(String(list[0].storeId));
      })
      .catch(() => { if (alive) setStores([]); });
    return () => { alive = false; };
  }, []);

  const storeQs = storeId ? `&storeId=${encodeURIComponent(storeId)}` : "";

  /* ── product search (debounced) ── */
  const runSearch = useCallback(async (q) => {
    const seq = ++searchSeq.current;
    setSearching(true);
    try {
      const url = `/admin/catalog/products?page=1&limit=25${q ? `&search=${encodeURIComponent(q)}` : ""}`
        + (storeId ? `&storeId=${encodeURIComponent(storeId)}` : "");
      const res = await api.get(url);
      /* the payload keeps its own {data, pagination} wrapper */
      const rows = res.data?.data ?? [];
      if (seq === searchSeq.current) {
        setResults(Array.isArray(rows) ? rows : []);
        setPageError("");
      }
    } catch (err) {
      if (seq === searchSeq.current) {
        setPageError(err.response?.data?.message || "Could not load products");
        setResults([]);
      }
    } finally {
      if (seq === searchSeq.current) setSearching(false);
    }
  }, [storeId]);

  useEffect(() => {
    const t = setTimeout(() => runSearch(query.trim()), query.trim() ? 300 : 0);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  /* ── adjustment log: every move against this product, both pools ── */
  const loadLog = useCallback(async (productId) => {
    setLogLoading(true);
    try {
      /* a BARE array, newest first */
      const res = await api.get(
        `/stock/moves?productId=${encodeURIComponent(productId)}&limit=25${storeQs}`
      );
      setLog(Array.isArray(res.data) ? res.data : []);
    } catch {
      setLog([]);
    } finally {
      setLogLoading(false);
    }
  }, [storeQs]);

  /* ── the bulk bin: {scope, grain, moves} for ONE product. 404 means the
        product is unknown, or does not sell in bulk at all. ── */
  const loadGrain = useCallback(async (productId) => {
    try {
      const res = await api.get(
        `/admin/bulk/grains/${productId}?limit=1${storeQs}`
      );
      setGrain(res.data?.grain ?? null);
    } catch {
      setGrain(null);
    }
  }, [storeQs]);

  /* ── packs on hand, per SKU, for the acting store ── */
  const loadBalances = useCallback(async (productId) => {
    if (!storeId) { setBalances({}); return; }
    try {
      const res = await api.get(
        `/stock/balances?productId=${encodeURIComponent(productId)}&storeId=${encodeURIComponent(storeId)}`
      );
      const map = {};
      (Array.isArray(res.data) ? res.data : []).forEach((b) => {
        if (b?.sku) map[b.sku] = Number(b.onHand) || 0;
      });
      setBalances(map);
    } catch {
      setBalances({});
    }
  }, [storeId]);

  /* ── select a product ── */
  const selectProduct = useCallback(async (row) => {
    setSelected(row);          // instant paint from the list row (no packs yet)
    setPackRows([]);
    setBalances({});
    setGrain(null);
    setRows({});
    setLog([]);
    setLoadingOne(true);
    try {
      const res = await api.get(`/admin/catalog/products/${row.id}?storeId=${encodeURIComponent(storeId)}`);
      const detail = res.data?.data;
      if (detail && detail.id) setSelected(detail);
      setPackRows(res.data?.packs ?? []);
    } catch (e) {
      setPageError(e.response?.data?.message || "Could not load that product");
    } finally {
      setLoadingOne(false);
    }
    await loadBalances(row.id);
    await loadGrain(row.id);
    await loadLog(row.id);
  }, [storeId, loadBalances, loadGrain, loadLog]);

  const refreshSelected = useCallback(async () => {
    if (!selected?.id) return;
    try {
      const res = await api.get(`/admin/catalog/products/${selected.id}?storeId=${encodeURIComponent(storeId)}`);
      const detail = res.data?.data;
      if (detail && detail.id) setSelected(detail);
      setPackRows(res.data?.packs ?? []);
    } catch { /* keep what we have */ }
    await loadBalances(selected.id);
    await loadGrain(selected.id);
  }, [selected, storeId, loadBalances, loadGrain]);

  /* ── derived view of the selection ── */
  const packs       = packsOf(packRows, balances);
  const hasVariants = packs.length > 0;
  const total       = totalOf(packs);

  const channels = selected ? channelsOf(selected) : { bulk: false, shelf: false };

  /* seed the price boxes from the live bin whenever the selection changes.
     Prices come back in PAISE and are edited here in rupees per kg. */
  useEffect(() => {
    setPriceMrp(grain?.mrpPaise != null ? String(grain.mrpPaise / 100) : "");
    setPriceSp(grain?.pricePerKgPaise != null ? String(grain.pricePerKgPaise / 100) : "");
    setPriceMsg("");
  }, [grain]);

  /* PUT the per-kg price onto the bin without touching its quantity. The
     backend enforces the same rules (both >= 0, selling <= MRP); mirroring
     them here keeps the admin from a pointless round trip. */
  const savePricing = async () => {
    if (!selected?.id || !grain) return;
    const mrp = Number(priceMrp);
    const sp  = Number(priceSp);
    if (!Number.isFinite(mrp) || mrp < 0) { setPriceMsg("Enter a valid MRP"); return; }
    if (!Number.isFinite(sp) || sp < 0)   { setPriceMsg("Enter a valid selling price"); return; }
    if (sp > mrp) { setPriceMsg("Selling price cannot exceed MRP"); return; }

    setPriceSaving(true);
    setPriceMsg("");
    try {
      /* rupees per kg — the server converts to paise and answers a real
         status: 400 bad_mrp/bad_price/price_above_mrp, 404 not_found,
         409 no_bulk_item or offer_ceiling (a cut that breaches the
         product's max offer against a live offer). */
      await api.put(`/admin/bulk/grains/${selected.id}/price`, {
        mrp, sellingPrice: sp,
      });
      await refreshSelected();
      setPriceMsg("Price updated");
    } catch (e) {
      setPriceMsg(e.response?.data?.message || "Could not update the price");
    } finally {
      setPriceSaving(false);
    }
  };

  /* the bin's own number, in KG — the server holds it in grams, and sends
     null when no store was named. NEVER added to the packs. */
  const bulkStock = grain?.onHandGrams != null ? grain.onHandGrams / 1000 : null;

  /* rows to render: one per pack, plus the bulk bin when the product sells
     loose. There is no third "base stock" pool any more — a product that
     sells neither way has nothing to count. */
  const editableRows = [
    ...packs,
    ...(channels.bulk
      ? [{
          key:    BULK_KEY,
          sku:    "",
          label:  "Bulk bin (kg)",
          stock:  bulkStock,
          isBulk: true,
        }]
      : []),
  ];

  const rowState = (key) => rows[key] || DEFAULT_ROW;
  /* patch off `prev`, never off the render-closure copy — applyRow fires
     several updates around awaits and must not resurrect stale fields */
  const setRow = (key, patch) =>
    setRows((prev) => ({ ...prev, [key]: { ...(prev[key] || DEFAULT_ROW), ...patch } }));

  /* ── the BULK write path ──────────────────────────────────────────
     The bin has endpoints of its own: an opening is an absolute recount,
     a restock is grams in. Both are addressed by PRODUCT id (there is no
     stock-entry document any more), both take GRAMS, and both REQUIRE a
     per-kg MRP and selling price — which is why the price boxes below
     have to be filled before a bin can be stocked at all.

     `store` is ignored for a store-bound account (it writes its own); a
     superadmin that names none falls back to the estate's default store. */
  const bulkApply = async (mode, kg, note) => {
    const mrp = Number(priceMrp);
    const sp  = Number(priceSp);
    if (!Number.isFinite(mrp) || !Number.isFinite(sp) || priceMrp === "" || priceSp === "") {
      throw new Error("Set the bulk per-kg MRP and selling price below before stocking the bin.");
    }
    await api.post(
      `/admin/bulk/grains/${selected.id}/${mode === "set" ? "opening" : "restock"}`,
      {
        quantity:     Math.round(kg * 1000),   // GRAMS on the wire
        mrp,                                   // rupees per kg
        sellingPrice: sp,
        store:        storeId || undefined,
        addedDate:    todayIso(),
        note,
      }
    );
  };

  /* ── apply one adjustment ──
     Packs go through the stock take (POST /admin/bulk/stock/apply) as a
     one-line batch; the bin goes through its own opening/restock. Neither
     write is possible without a store: the take refuses a superadmin who
     names none, and the bin would land in the wrong branch. */
  const applyRow = async (row) => {
    if (!selected?.id) return;
    const st  = rowState(row.key);
    const qty = Number(st.qty);

    if (!storeId) {
      setRow(row.key, { err: "Pick the store this count belongs to first.", msg: "" });
      return;
    }
    if (st.qty === "" || !Number.isFinite(qty) || qty < 0) {
      setRow(row.key, { err: "Enter a quantity of 0 or more", msg: "" });
      return;
    }
    if (st.mode === "add" && qty === 0) {
      setRow(row.key, { err: "Add quantity must be more than 0", msg: "" });
      return;
    }
    if (st.mode === "set" && qty === 0 &&
        !window.confirm(
          row.isBulk
            ? `Set "${row.label}" to 0? The bulk bin will read empty.`
            : `Set "${row.label}" to 0? This takes the pack off the shelf.`
        )) {
      return;
    }
    /* a PACK is addressed by SKU — the take refuses a packs line without one */
    if (!row.isBulk && !row.sku) {
      setRow(row.key, {
        err: "This pack has no SKU code — add one on the product before adjusting its stock.",
        msg: "",
      });
      return;
    }

    setRow(row.key, { saving: true, err: "", msg: "" });
    const note = (st.note || "").trim();

    try {
      if (row.isBulk) {
        await bulkApply(st.mode, qty, note);
      } else {
        /* One row is a one-line batch. PARTIAL SUCCESS IS A 200: a refused
           line comes back in failed[] rather than as a thrown error. */
        const res = await api.post("/admin/bulk/stock/apply", {
          storeId,
          mode:  st.mode,
          note:  note || undefined,
          lines: [{
            code:     row.sku || selected.productCode || selected.name,
            product:  selected.id,
            pool:     "packs",
            skuCode:  row.sku,
            quantity: qty,                    // PACKS
          }],
        });
        const refused = res.data?.failed?.[0];
        if (refused) {
          setRow(row.key, { saving: false, err: refused.reason || "That line was refused", msg: "" });
          return;
        }
      }

      setRow(row.key, {
        saving: false, qty: "", note: "", err: "",
        msg: st.mode === "set" ? `Set to ${qty}` : `Added ${qty}`,
      });
      await refreshSelected();
      await loadLog(selected.id);
    } catch (err) {
      /* a real HTTP status with {code, message} — no status field to read */
      setRow(row.key, {
        saving: false,
        err: err.response?.data?.message || err.message || "Update failed",
      });
    }
  };

  /* ── log rows ──────────────────────────────────────────────────────
     A move's quantity is always POSITIVE; the direction is in the two
     location codes, so an inbound move is one whose `to` is this store.
     Bulk moves are grams and pack moves are packs — the row does not say
     which, so it is decided by the SKU ('' or 'BULK' is the bin). */
  const storeCode = storeId ? `STORE:${storeId}` : "";
  const logRows = (log || []).map((r) => {
    const isBulk   = !r.sku || r.sku === "BULK";
    const inbound  = storeCode ? r.to === storeCode : null;
    const outbound = storeCode ? r.from === storeCode : null;
    return {
      id:      r.id,
      at:      r.effectiveAt || r.recordedAt,
      sku:     isBulk ? "bulk bin" : r.sku,
      kind:    MOVE_LABEL[r.moveType] || String(r.moveType || "").toUpperCase() || "MOVE",
      /* grams as they are stored; packs as they are counted */
      qty:     Number(r.qty) || 0,
      unit:    isBulk ? " g" : "",
      sign:    inbound ? "+" : outbound ? "−" : "",
      /* qty_after_to is the balance at the DESTINATION, so it only reads as
         "this store's new count" on an inbound move */
      after:   inbound ? r.qtyAfterTo : null,
      by:      r.actor,
      note:    r.reason,
    };
  });

  /* ═══════════════ RENDER ═══════════════ */
  return (
    <div className="container-fluid">
      <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>

        {/* ── header ── */}
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 12 }}>
          <div>
            <div style={{
              fontSize: 10.5, fontWeight: 700, color: BRAND.gold,
              textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 2,
            }}>
              Stock Manager
            </div>
            <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: BRAND.forest }}>
              Set or add stock, pack by pack
            </h4>
            <div style={{ fontSize: 12, color: BRAND.stone, marginTop: 3 }}>
              Numbers below are the live shelf — orders decrement them and cancellations put them back.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {/* the store every read and write on this screen is scoped to.
                One store means it is the only one this account may count —
                it is pinned above and there is nothing to choose. */}
            {stores.length > 1 && (
              <select
                value={storeId}
                onChange={(e) => { setStoreId(e.target.value); setSelected(null); }}
                style={{
                  fontSize: 13, padding: "6px 10px", borderRadius: 8,
                  border: `1px solid ${BRAND.sand}`, background: "var(--mly-surface)",
                  color: BRAND.ink, fontWeight: 600,
                }}
              >
                <option value="">Pick a store…</option>
                {stores.map((s) => (
                  <option key={s.storeId} value={s.storeId}>
                    {s.name}{s.code ? ` · ${s.code}` : ""}{s.isActive === false ? " (closed)" : ""}
                  </option>
                ))}
              </select>
            )}
            <button
              className="btn btn-sm"
              style={{ background: BRAND.cream, color: BRAND.forest, border: `1px solid ${BRAND.sand}`, fontWeight: 700 }}
              onClick={() => navigate("/admin/products")}
            >
              ← Products
            </button>
            {selected && (
              <button
                className="btn btn-sm"
                style={{ background: "var(--mly-accent)", color: "var(--mly-on-accent)", border: "none", fontWeight: 700 }}
                onClick={refreshSelected}
              >
                Refresh
              </button>
            )}
          </div>
        </div>

        <div className="card-body">
          {pageError && <div className="alert alert-danger">{pageError}</div>}
          {stores.length > 1 && !storeId && (
            <div className="alert alert-warning" style={{ fontSize: 13 }}>
              Stock is counted per store. Pick a store above — until then the
              numbers below are blank and nothing can be applied.
            </div>
          )}

          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-start" }}>

            {/* ══ search panel ══ */}
            <div style={{ flex: "1 1 320px", minWidth: 300, maxWidth: 420 }}>
              <label style={LABEL}>Find a product</label>
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                border: `1px solid ${BRAND.sand}`, borderRadius: 10,
                padding: "8px 12px", background: "var(--mly-surface)", marginTop: 6,
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="search-icon" aria-hidden="true"><path d="M10.6 2.4a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4Zm0 2.2a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm5.9 11.3 4.8 4.8a1.2 1.2 0 0 1-1.7 1.7l-4.8-4.8a9.6 9.6 0 0 0 1.7-1.7Z" /></svg>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name or product code…"
                  style={{ border: "none", outline: "none", width: "100%", fontSize: 13.5, background: "transparent" }}
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    style={{ border: "none", background: "transparent", color: BRAND.stone, cursor: "pointer", fontSize: 16, lineHeight: 1 }}
                  >
                    ×
                  </button>
                )}
              </div>

              <div style={{
                marginTop: 10, border: `1px solid ${BRAND.sand}`, borderRadius: 12,
                overflow: "hidden", background: "var(--mly-surface)",
              }}>
                <div style={{
                  padding: "7px 12px", background: BRAND.cream, fontSize: 11,
                  fontWeight: 700, color: BRAND.stone, letterSpacing: 0.5,
                  textTransform: "uppercase", borderBottom: `1px solid ${BRAND.sand}`,
                }}>
                  {searching ? "Searching…" : `${results.length} product${results.length === 1 ? "" : "s"}`}
                </div>
                <div style={{ maxHeight: 460, overflowY: "auto" }}>
                  {results.length === 0 && !searching && (
                    <div style={{ padding: "26px 14px", textAlign: "center", color: "var(--mly-text-muted)", fontSize: 13 }}>
                      No products found
                    </div>
                  )}
                  {results.map((p) => {
                    /* the list row carries no per-pack breakdown: packCount is
                       how many pack SKUs it has, packsOnHand the store's total
                       (0 when no store was named) */
                    const packCount = Number(p.packCount) || 0;
                    const rowTotal  = p.sellsPrepacked && storeId ? (num(p.packsOnHand) ?? null) : null;
                    const active    = selected?.id === p.id;
                    return (
                      <div
                        key={p.id}
                        onClick={() => selectProduct(p)}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          gap: 10, padding: "10px 12px", cursor: "pointer",
                          borderBottom: `1px solid ${BRAND.cream}`,
                          background: active ? BRAND.cream : "var(--mly-surface)",
                          borderLeft: `3px solid ${active ? BRAND.gold : "transparent"}`,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{
                            fontSize: 13, fontWeight: 700, color: BRAND.ink,
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          }}>
                            {p.name}
                          </div>
                          <div style={{ fontSize: 11, color: BRAND.stone, marginTop: 2 }}>
                            {p.productCode || "—"}
                            {packCount > 0 && ` · ${packCount} pack${packCount > 1 ? "s" : ""}`}
                          </div>
                        </div>
                        <StockPill value={rowTotal} threshold={threshold} size={12} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ══ editor panel ══ */}
            <div style={{ flex: "2 1 520px", minWidth: 340 }}>
              {!selected ? (
                <div style={{
                  border: `1px dashed ${BRAND.sand}`, borderRadius: 14, padding: "60px 20px",
                  textAlign: "center", color: BRAND.stone, background: BRAND.cream,
                }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: BRAND.forest, marginBottom: 6 }}>
                    Pick a product to manage its stock
                  </div>
                  <div style={{ fontSize: 12.5 }}>
                    Prepacked products show one row per pack size. Raw materials show a single base balance.
                  </div>
                </div>
              ) : (
                <>
                  {/* selected product header */}
                  <div style={{
                    border: `1px solid ${BRAND.sand}`, borderRadius: 14,
                    padding: "14px 16px", background: "var(--mly-surface)",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: BRAND.forest }}>
                          {selected.name}
                          {loadingOne && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: BRAND.stone, marginLeft: 8 }}>
                              loading…
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: BRAND.stone, marginTop: 3, display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <span>{selected.productCode || "no code"}</span>
                          <span>·</span>
                          <span>{selected.categoryName ?? "—"}</span>
                          <span>·</span>
                          <span style={{
                            background: channels.shelf && channels.bulk ? BRAND.cream : channels.shelf ? "#f0fdf4" : "#fff7ed",
                            border: `1px solid ${channels.shelf && channels.bulk ? BRAND.gold : channels.shelf ? "#bbf7d0" : "#fed7aa"}`,
                            borderRadius: 20, padding: "1px 9px", fontSize: 10.5, fontWeight: 700,
                            color: channels.shelf && channels.bulk ? BRAND.forest : channels.shelf ? "#16a34a" : "#b45309",
                          }}>
                            {channels.shelf && channels.bulk
                              ? "Shelf + Bulk"
                              : channels.shelf
                              ? "Shelf"
                              : channels.bulk
                              ? "Bulk"
                              : "no sale channel"}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 18, textAlign: "right" }}>
                        {channels.shelf && (
                          <div>
                            <div style={{ ...LABEL, marginBottom: 4 }}>Packs on shelf</div>
                            <StockPill value={total} threshold={threshold} size={15} />
                          </div>
                        )}
                        {channels.bulk && (
                          <div>
                            <div style={{ ...LABEL, marginBottom: 4 }}>Bulk bin (kg)</div>
                            <StockPill value={bulkStock} threshold={threshold} size={15} />
                          </div>
                        )}
                      </div>
                    </div>

                    {channels.shelf && channels.bulk && (
                      <div style={{
                        marginTop: 10, paddingTop: 10, borderTop: `1px solid ${BRAND.sand}`,
                        fontSize: 11.5, color: BRAND.stone, lineHeight: 1.5,
                      }}>
                        Two independent pools: packs are counted in units, the bulk bin in kg.
                        Set each one on its own — stock is never moved between them.
                      </div>
                    )}
                  </div>

                  {/* pack editor */}
                  <div style={{
                    marginTop: 14, border: `1px solid ${BRAND.sand}`, borderRadius: 14,
                    overflow: "hidden", background: "var(--mly-surface)",
                  }}>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
                        <thead>
                          <tr style={{ background: BRAND.cream }}>
                            <th style={TH}>Pack / pool</th>
                            <th style={TH}>Current</th>
                            <th style={TH}>Mode</th>
                            <th style={TH}>Quantity</th>
                            <th style={TH}>Note</th>
                            <th style={TH} />
                          </tr>
                        </thead>
                        <tbody>
                          {editableRows.map((row) => {
                            const st = rowState(row.key);
                            return (
                              <tr key={row.key} style={{ borderBottom: `1px solid ${BRAND.cream}` }}>
                                <td style={TD}>
                                  <div style={{ fontWeight: 700 }}>{row.label}</div>
                                  {row.sku && (
                                    <div style={{ fontSize: 10.5, color: BRAND.stone, marginTop: 2 }}>{row.sku}</div>
                                  )}
                                </td>
                                <td style={TD}>
                                  <StockPill value={row.stock} threshold={threshold} size={12.5} />
                                </td>
                                <td style={TD}>
                                  <div style={{ display: "inline-flex", border: `1px solid ${BRAND.sand}`, borderRadius: 8, overflow: "hidden" }}>
                                    {["set", "add"].map((m) => (
                                      <button
                                        key={m}
                                        type="button"
                                        onClick={() => setRow(row.key, { mode: m, err: "" })}
                                        style={{
                                          border: "none", cursor: "pointer",
                                          padding: "5px 12px", fontSize: 11.5, fontWeight: 700,
                                          background: st.mode === m ? "var(--mly-accent)" : "var(--mly-surface)",
                                          color:      st.mode === m ? "var(--mly-on-accent)"  : BRAND.stone,
                                        }}
                                      >
                                        {m === "set" ? "Set" : "Add"}
                                      </button>
                                    ))}
                                  </div>
                                </td>
                                <td style={TD}>
                                  <input
                                    type="number"
                                    min={st.mode === "add" ? 1 : 0}
                                    value={st.qty}
                                    onChange={(e) => setRow(row.key, { qty: e.target.value, err: "", msg: "" })}
                                    placeholder={st.mode === "set" ? "new count" : "+ packs"}
                                    style={{
                                      width: 100, padding: "6px 10px", fontSize: 13,
                                      border: `1px solid ${st.err ? "#ef4444" : BRAND.sand}`,
                                      borderRadius: 8, outline: "none",
                                    }}
                                  />
                                </td>
                                <td style={TD}>
                                  <input
                                    type="text"
                                    value={st.note}
                                    onChange={(e) => setRow(row.key, { note: e.target.value })}
                                    placeholder="optional"
                                    style={{
                                      width: "100%", minWidth: 120, padding: "6px 10px", fontSize: 12.5,
                                      border: `1px solid ${BRAND.sand}`, borderRadius: 8, outline: "none",
                                    }}
                                  />
                                </td>
                                <td style={{ ...TD, whiteSpace: "nowrap" }}>
                                  <button
                                    type="button"
                                    onClick={() => applyRow(row)}
                                    disabled={st.saving}
                                    style={{
                                      border: "none", borderRadius: 9, padding: "7px 16px",
                                      fontSize: 12.5, fontWeight: 700, cursor: st.saving ? "not-allowed" : "pointer",
                                      background: st.saving ? BRAND.stone : BRAND.pine, color: "var(--mly-on-accent)",
                                    }}
                                  >
                                    {st.saving ? "Saving…" : "Apply"}
                                  </button>
                                  {st.err && (
                                    <div style={{ color: "#ef4444", fontSize: 11, marginTop: 5, maxWidth: 220, whiteSpace: "normal" }}>
                                      {st.err}
                                    </div>
                                  )}
                                  {st.msg && !st.err && (
                                    <div style={{ color: "#16a34a", fontSize: 11, marginTop: 5, whiteSpace: "normal" }}>
                                      {st.msg}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* ── Bulk per-kg price ──
                        The product form shows these read-only and sends the
                        admin here, so this is the one place a bulk price can
                        actually be changed. Quantity is untouched. */}
                    {channels.bulk && grain && (
                      <div style={{
                        padding: "14px 16px",
                        borderTop: `1px solid var(--mly-border)`,
                        background: "var(--mly-surface-2)",
                      }}>
                        <div style={{
                          fontSize: 11, fontWeight: 800, letterSpacing: 1,
                          textTransform: "uppercase", color: "var(--mly-text-muted)",
                          marginBottom: 10,
                        }}>
                          Bulk price — per kg
                        </div>
                        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                          <div>
                            <label style={{ display: "block", fontSize: 11, color: "var(--mly-text-muted)", marginBottom: 4 }}>
                              MRP / kg
                            </label>
                            <input
                              type="number" min="0" step="0.01" value={priceMrp}
                              onChange={(e) => setPriceMrp(e.target.value)}
                              style={{
                                width: 120, padding: "9px 12px", borderRadius: 10,
                                border: "1.5px solid var(--mly-border)", fontSize: 14,
                                background: "var(--mly-surface)", color: "var(--mly-text)",
                              }}
                            />
                          </div>
                          <div>
                            <label style={{ display: "block", fontSize: 11, color: "var(--mly-text-muted)", marginBottom: 4 }}>
                              Selling / kg
                            </label>
                            <input
                              type="number" min="0" step="0.01" value={priceSp}
                              onChange={(e) => setPriceSp(e.target.value)}
                              style={{
                                width: 120, padding: "9px 12px", borderRadius: 10,
                                border: "1.5px solid var(--mly-border)", fontSize: 14,
                                background: "var(--mly-surface)", color: "var(--mly-text)",
                              }}
                            />
                          </div>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={savePricing}
                            disabled={priceSaving}
                            style={{ height: 38 }}
                          >
                            {priceSaving ? "Saving…" : "Update price"}
                          </button>
                          {priceMsg && (
                            <span style={{
                              fontSize: 12, fontWeight: 600,
                              color: priceMsg === "Price updated"
                                ? "var(--mly-heading)" : "#C62828",
                            }}>
                              {priceMsg}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 8 }}>
                          Changes the bin's price without touching its quantity — and it applies
                          to the WHOLE bin from now: there is no per-batch price any more, so a
                          restock no longer leaves the previous lot selling at its old number.
                        </div>
                      </div>
                    )}

                    {!hasVariants && channels.bulk && (
                      <div style={{
                        padding: "9px 14px", fontSize: 11.5, color: BRAND.stone,
                        background: BRAND.cream, borderTop: `1px solid ${BRAND.sand}`,
                      }}>
                        This product has no packs — it is counted by weight in the bin and
                        milled to order.
                      </div>
                    )}
                  </div>

                  {/* adjustment log */}
                  <div style={{
                    marginTop: 16, border: `1px solid ${BRAND.sand}`, borderRadius: 14,
                    overflow: "hidden", background: "var(--mly-surface)",
                  }}>
                    <div style={{
                      padding: "9px 14px", background: BRAND.cream,
                      borderBottom: `1px solid ${BRAND.sand}`,
                      display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                    }}>
                      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: BRAND.stone }}>
                        Recent adjustments
                      </span>
                      <span style={{ fontSize: 10.5, color: BRAND.stone }}>
                        {logLoading ? "loading…" : "newest first"}
                      </span>
                    </div>
                    <div style={{ overflowX: "auto", maxHeight: 320, overflowY: "auto" }}>
                      {logRows.length === 0 ? (
                        <div style={{ padding: "26px 14px", textAlign: "center", color: "var(--mly-text-muted)", fontSize: 13 }}>
                          No adjustments recorded yet
                        </div>
                      ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
                          <thead>
                            <tr style={{ background: "var(--mly-surface)" }}>
                              <th style={TH}>When</th>
                              <th style={TH}>Pack</th>
                              <th style={TH}>Change</th>
                              {/* the move log records the balance AFTER the move at
                                  its destination; there is no "before" column */}
                              <th style={TH}>After</th>
                              <th style={TH}>By</th>
                              <th style={TH}>Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {logRows.map((r, i) => {
                              const out = r.sign === "−";
                              return (
                                <tr key={r.id || `${r.at}-${i}`} style={{ borderBottom: `1px solid ${BRAND.cream}` }}>
                                  <td style={{ ...TD, whiteSpace: "nowrap", color: BRAND.stone, fontSize: 12 }}>
                                    {formatWhen(r.at)}
                                  </td>
                                  <td style={{ ...TD, fontSize: 12 }}>{r.sku}</td>
                                  <td style={{ ...TD, whiteSpace: "nowrap" }}>
                                    <span style={{
                                      background: out ? "#fff5f5" : BRAND.cream,
                                      color:      out ? "#b91c1c" : BRAND.forest,
                                      border: `1px solid ${out ? "#fecaca" : BRAND.gold}`,
                                      borderRadius: 20, padding: "2px 9px",
                                      fontSize: 10.5, fontWeight: 800, marginRight: 6,
                                    }}>
                                      {r.kind}
                                    </span>
                                    <strong>{r.sign}{r.qty}{r.unit}</strong>
                                  </td>
                                  <td style={{ ...TD, fontSize: 12, whiteSpace: "nowrap", color: BRAND.stone }}>
                                    {r.after === null || r.after === undefined ? "—" : `${r.after}${r.unit}`}
                                  </td>
                                  <td style={{ ...TD, fontSize: 12 }}>{r.by || "—"}</td>
                                  <td style={{ ...TD, fontSize: 12, color: BRAND.stone }}>{r.note || "—"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockEntry;
